import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCard, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { StorageService, UserService } from '@shared/services/index';
import { SessionKeys, StorageKeys, StorageType, ValidRoles } from '../../../shared/enums';
import { Router } from '@angular/router';
import { CentroMedico, Empresa, Medicos, User } from '@shared/interfaces/perfil.interface';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { HeaderSophiaComponent } from '../../common/components/header-sophia/header-sophia.component';
import { InputSearchFormFieldComponent } from '../../../shared/components/input-search-form-field/input-search-form-field.component';
import { FormControl } from '@angular/forms';
import { SelectFormFieldComponent } from '../../../shared/components/select-form-field/select-form-field.component';
import { PerfilService } from '@shared/services/perfil.service';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  lastValueFrom,
  Subscription,
  take,
} from 'rxjs';
import { AuthenticationService } from '../../services/authentication.service';
import { DatosMedicosService } from '@shared/services/datos-medicos.service';
import { ROUTES_APP } from '../../../app.routes';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ModalSpinnerComponent } from '../../../shared/components/modal-spinner/modal-spinner.component';

@Component({
  selector: 'soph-medical-centers',
  standalone: true,
  imports: [
    MatButtonModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatIcon,
    MatFormFieldModule,
    HeaderSophiaComponent,
    InputSearchFormFieldComponent,
    SelectFormFieldComponent,
    MatProgressSpinnerModule,
    ModalSpinnerComponent,
  ],
  templateUrl: './medical-centers.component.html',
  styleUrl: './medical-centers.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MedicalCentersComponent {
  private readonly perfil = inject(PerfilService);
  private readonly medicos = inject(DatosMedicosService);

  private readonly router = inject(Router);
  private readonly auth = inject(AuthenticationService);
  private readonly user = inject(UserService);
  private readonly storage = inject(StorageService);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly searchControl: FormControl = new FormControl('');
  protected readonly companiesControl: FormControl = new FormControl('');
  protected readonly doctorControl: FormControl = new FormControl('');

  protected companyOptions: WritableSignal<Array<Empresa>> = signal([]);
  protected doctorOptions: WritableSignal<Array<Medicos>> = signal([]);
  protected medicalCenters: WritableSignal<Array<CentroMedico>> = signal([]);
  protected searchQuery: WritableSignal<string> = signal('');

  protected filteredMedicalCenters = computed(() => {
    const currentValue = this.searchQuery() ? this.searchQuery().toLowerCase() : '';

    return currentValue
      ? this.medicalCenters().filter((value) => value.nombre.toLowerCase().includes(currentValue))
      : this.medicalCenters();
  });

  protected readonly isLoading = signal(true);
  protected readonly isMedic = signal(false);
  protected readonly isAssistant = signal(false);

  private subscriptions: Subscription[] = [];

  constructor() {
    this.resetValuesLS();

    this.iconRegistry.addSvgIcon(
      'hospital',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons-v2/hospital.svg`)
    );

    this.iconRegistry.addSvgIcon(
      'search',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons-v2/search.svg`)
    );

    this.iconRegistry.addSvgIcon(
      'close',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons-v2/close.svg`)
    );

    const user$ = this.user.user
      .pipe(
        filter((user) => user !== undefined && user !== null),
        take(1)
      )
      .subscribe((user) => {
        if (user) {
          if (this.auth.hasRole(ValidRoles.MEDIC)) {
            this.isMedic.set(true);
            this.loadDataMedicalCenters();
            return;
          }

          if (
            this.auth.hasRole(ValidRoles.ASSISTANT) ||
            this.auth.hasRole(ValidRoles.MEDICAL_ASSISTANT)
          ) {
            this.isAssistant.set(true);
            this.loadDataDoctors();
          }
        }
      });

    const search$ = this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      this.searchQuery.set(value);
    });

    const companies$ = this.companiesControl.valueChanges.subscribe(() => {
      if (this.isMedic()) {
        this.loadDataMedicalCenters();
        return;
      }

      if (this.isAssistant()) {
        this.loadDataDoctors();
      }
    });

    const doctor$ = this.doctorControl.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.loadDataMedicalCenters();
    });

    this.subscriptions.push(user$, search$, companies$, doctor$);
  }

  ngOnInit(): void {
    this.loadDataCompanies();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private loadDataCompanies(): void {
    const companies$ = this.perfil.obtenerEmpresas();

    lastValueFrom(companies$, { defaultValue: null }).then((response) => {
      if (response) {
        this.companyOptions.set(response.data ?? []);
        this.companiesControl.setValue(this.companyOptions()?.[0].idEmpresa ?? '');
      }
    });
  }

  private loadDataDoctors(): void {
    const doctors$ = this.medicos.obtenerMedicosAsistentePorEmpresa(this.companiesControl.value);

    lastValueFrom(doctors$, { defaultValue: null }).then((response) => {
      if (response) {
        this.doctorOptions.set(response.data ?? []);

        this.doctorControl.setValue(
          this.doctorOptions().length > 0 ? this.doctorOptions()?.[0].idMedico : ''
        );
      }
    });
  }

  private loadDataMedicalCenters(): void {
    this.isLoading.set(true);

    if (!this.getDoctorId() || !this.companiesControl.value) {
      this.isLoading.set(true);
      return;
    }

    const medicalCenters$ = this.medicos.obtenerCentrosMedicosPorMedico(
      this.getDoctorId()!,
      this.companiesControl.value
    );

    lastValueFrom(medicalCenters$, { defaultValue: null })
      .then((response) => {
        if (response) {
          this.medicalCenters.set(response.data ?? []);
        }
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  private getDoctor(): Medicos | undefined {
    return this.isMedic()
      ? this.storage.getItem<User>(SessionKeys.USER, StorageType.SESSION)?.medicos
      : this.doctorOptions().find((doc) => doc.idMedico === this.doctorControl.value);
  }

  private getDoctorId(): string | undefined {
    return this.isMedic() ? this.getDoctor()?.idMedico : this.doctorControl.value;
  }

  protected goToScheduleAppointment(medicalCenter: CentroMedico): void {
    this.setValuesLS(medicalCenter);
    this.router.navigate([ROUTES_APP.CALENDARIO], { replaceUrl: true });
  }

  private setValuesLS(medicalCenter: CentroMedico): void {
    this.storage.setItem(StorageKeys.CURRENT_MEDICAL_CENTER, medicalCenter, StorageType.LOCAL);
    this.storage.setItem(StorageKeys.CURRENT_DOCTOR, this.getDoctor(), StorageType.LOCAL);
  }

  private resetValuesLS(): void {
    this.storage.removeItem(StorageKeys.CURRENT_MEDICAL_CENTER, StorageType.LOCAL);
    this.storage.removeItem(StorageKeys.CURRENT_DOCTOR, StorageType.LOCAL);
  }
}
